import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { MatMenuTrigger } from '@angular/material/menu';

export interface ReactionHover {
  emoji: string;
  trigger: MatMenuTrigger;
}

/**
 * One chat message (main feed or thread). Presentation only: every action is
 * emitted and the parent calls the inherited CommunityMaincontentComponent handler.
 */
@Component({
  selector: 'app-cm-message',
  templateUrl: './cm-message.component.html',
  styleUrls: ['./cm-message.component.scss'],
})
export class CmMessageComponent {
  @Input() message: any;
  @Input() time = '';
  @Input() grouped = false;
  @Input() isThread = false;
  @Input() currentUserId = '';
  @Input() canVote = true;
  @Input() emojiOpen = false;
  @Input() reactionUsers: any[] = [];
  @Input() hoveredReaction: any;

  @Output() toggleEmoji = new EventEmitter<void>();
  @Output() pickEmoji = new EventEmitter<any>();
  @Output() react = new EventEmitter<string>();
  @Output() openThread = new EventEmitter<void>();
  @Output() reportUser = new EventEmitter<void>();
  @Output() reportMessage = new EventEmitter<void>();
  @Output() toggleExpand = new EventEmitter<void>();
  @Output() openImage = new EventEmitter<string>();
  @Output() download = new EventEmitter<any>();
  @Output() vote = new EventEmitter<string>();
  /** New text for the sender's own message. */
  @Output() edit = new EventEmitter<string>();

  editing = false;
  draft = '';

  @ViewChild('editBox') set editBox(box: ElementRef<HTMLTextAreaElement> | undefined) {
    box?.nativeElement.focus();
  }
  @Output() reactionEnter = new EventEmitter<ReactionHover>();
  @Output() reactionLeave = new EventEmitter<MatMenuTrigger>();

  get mine(): boolean {
    return !!this.message?.sentMsg;
  }

  /** Own polls are shown as the poll card itself, not inside a chat bubble. */
  get isPoll(): boolean {
    return this.message?.type === 'poll';
  }

  get blocked(): boolean {
    return !!this.message?.isBlocked;
  }

  get sender(): any {
    return this.message?._sender || {};
  }

  get showAvatarPhoto(): boolean {
    return !!this.sender.profileUrl && !this.message?.blockedUser;
  }

  /** Same visibility rules as the classic page's report menu. */
  get canReportUser(): boolean {
    return !this.mine && !this.message?.blockedUser && this.message?.userType !== 'creator';
  }

  /** Senders can edit their own text messages (not polls or blocked messages). */
  get canEdit(): boolean {
    return this.mine && !this.blocked && !this.isPoll && !!this.message?.message;
  }

  startEdit(): void {
    this.draft = this.message.message;
    this.editing = true;
  }

  cancelEdit(): void {
    this.editing = false;
  }

  saveEdit(): void {
    const text = this.draft.trim();
    if (text && text !== this.message.message) this.edit.emit(text);
    this.editing = false;
  }

  onEditKey(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.cancelEdit();
    } else if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.saveEdit();
    }
  }

  get canReportMessage(): boolean {
    return !this.mine && !this.blocked;
  }

  get isLong(): boolean {
    return (this.message?.message?.length || 0) > 1000;
  }

  get reactionKeys(): string[] {
    return this.message?.reactions ? Object.keys(this.message.reactions) : [];
  }

  reactedByMe(emoji: string): boolean {
    const users = this.message?.reactions?.[emoji]?.users || [];
    return users.some((u: any) => u?.userId === this.currentUserId);
  }

  get replyCount(): number {
    return this.message?.threadReplies?.length || 0;
  }

  shortName(name: any): string {
    const s = String(name || '');
    return s.length > 14 ? s.slice(0, 11) + '...' : s;
  }
}
